import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { format, startOfDay } from "date-fns";
import { ArrowDownRight, ArrowRight, ArrowUpRight, CalendarDays, Coins, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import type { PageResponse } from "@/services/inquiryService";

/**
 * Search is debounced INSIDE this component (~300ms) so callers can pass a
 * plain state setter as `onSearchChange` without implementing their own
 * debounce. `searchTerm` remains the source of truth for the true-empty vs.
 * no-results decision (it reflects what's actually been requested from the
 * server, not every keystroke).
 */
const SEARCH_DEBOUNCE_MS = 300;
const SKELETON_ROW_COUNT = 5;

export interface CoinSpendRow {
  /** Optional — only populated by the student caller (`StudentCoinSpendRow`). Falls back to `threadId` for the teacher caller, which never supplies it. */
  id?: string;
  /** null when the row has no associated inquiry thread (e.g. an admin coin-balance adjustment). */
  threadId: string | null;
  /** null renders as "—" (D4 — a ledger row whose thread failed to resolve a name). */
  counterpartName: string | null;
  /** ISO instant string — the "Initial Date of inquiry" (ADR 083: the charge date). */
  createdAt: string;
  /**
   * Sign-aware: negative for a charge, positive for a hold refund, zero for a
   * (non-monetary) hold-cleared entry. Rendered accordingly — see the amount
   * cell below.
   */
  amount: number;
  balanceAfter: number;
  /**
   * Optional — only populated by the student caller. Drives the "Type"
   * column, which is hidden entirely when no row on the page has a reason
   * (i.e. for the teacher caller).
   */
  reason?: string;
}

/** Human-readable label for a ledger row's `reason`. Falls back to the raw value for any reason not yet mapped here. */
const REASON_LABELS: Record<string, string> = {
  INQUIRY_CHARGE: "Charge",
  INQUIRY_HOLD: "Hold",
  INQUIRY_HOLD_CLEARED: "Cleared",
  INQUIRY_HOLD_REFUNDED: "Refunded",
  REGISTRATION_GRANT: "Bonus",
  PURCHASE: "Top-up",
  DISPUTE_CLAWBACK: "Dispute",
  REFUND: "Refund",
};

/**
 * `ADMIN_ADJUSTMENT` is sign-aware rather than a fixed label: a positive
 * amount is a credit to the user, a non-positive amount (including exactly
 * zero — a real, reachable case per the backend's design, not a bug) reads
 * as a support-initiated adjustment.
 */
function formatReasonLabel(reason: string, amount: number): string {
  if (reason === "ADMIN_ADJUSTMENT") {
    return amount > 0 ? "Support Credit" : "Support Adjustment";
  }
  return REASON_LABELS[reason] ?? reason;
}

export interface CoinSpendTableProps {
  /** Column header + search placeholder label, e.g. "Name of the teacher". */
  counterpartLabel: string;
  page: PageResponse<CoinSpendRow> | undefined;
  isLoading: boolean;
  isError: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  onDateFromChange: (date: Date | undefined) => void;
  onDateToChange: (date: Date | undefined) => void;
  pageIndex: number;
  onPageChange: (page: number) => void;
  onRowClick: (row: CoinSpendRow) => void;
  /**
   * Floor for both "From date" and "To date" pickers — neither may go earlier
   * than the caller's registration date. Undefined while the caller's
   * registration date hasn't loaded yet (no floor applied in that window);
   * "today" is always applied as the ceiling regardless.
   */
  registrationDate?: Date;
}

const ELLIPSIS = "ellipsis" as const;
type PageToken = number | typeof ELLIPSIS;

/** Builds a compact page-number window: first, last, and a small run around current. */
function buildPageWindow(current: number, totalPages: number): PageToken[] {
  if (totalPages <= 0) return [];
  const keep = new Set<number>();
  keep.add(0);
  keep.add(totalPages - 1);
  for (let p = current - 1; p <= current + 1; p++) {
    if (p >= 0 && p < totalPages) keep.add(p);
  }
  const sorted = Array.from(keep).sort((a, b) => a - b);
  const tokens: PageToken[] = [];
  let prev = -1;
  for (const p of sorted) {
    if (prev !== -1 && p - prev > 1) tokens.push(ELLIPSIS);
    tokens.push(p);
    prev = p;
  }
  return tokens;
}

function formatSpendDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
}

type DateMatcher = { before: Date } | { after: Date };

interface DatePickerFieldProps {
  label: string;
  placeholder: string;
  date: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  disabledMatcher?: DateMatcher | DateMatcher[];
}

function DatePickerField({ label, placeholder, date, onSelect, disabledMatcher }: DatePickerFieldProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={label}
          className={cn(
            "h-10 w-full sm:w-auto min-w-[9.5rem] cursor-pointer justify-start gap-2 font-normal motion-safe:transition-colors",
            !date && "text-muted-foreground",
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{date ? format(date, "MMM d, yyyy") : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onSelect}
          disabled={disabledMatcher}
          initialFocus
        />
        {date && (
          <div className="border-t border-border p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full cursor-pointer text-xs"
              onClick={() => onSelect(undefined)}
            >
              Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function CoinSpendTable({
  counterpartLabel,
  page,
  isLoading,
  isError,
  searchTerm,
  onSearchChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  pageIndex,
  onPageChange,
  onRowClick,
  registrationDate,
}: CoinSpendTableProps) {
  // Local input state gives instant keystroke feedback; the debounced call
  // below is what actually reaches the parent/API.
  const [searchInput, setSearchInput] = useState(searchTerm);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const rows = page?.content ?? [];
  const totalElements = page?.totalElements ?? 0;
  const totalPages = page?.totalPages ?? 0;
  const currentPage = page?.number ?? pageIndex;

  const hasActiveFilters = searchTerm.trim().length > 0 || dateFrom !== undefined || dateTo !== undefined;
  const loadedEmpty = !isLoading && !isError && page !== undefined && totalElements === 0;
  const showTrueEmpty = loadedEmpty && !hasActiveFilters;
  const showNoResults = loadedEmpty && hasActiveFilters;

  const noResultsMessage = searchTerm.trim()
    ? `No transactions match "${searchTerm.trim()}".`
    : "No transactions match the selected date range.";

  const handleRowActivate = (row: CoinSpendRow) => onRowClick(row);

  const pageTokens = buildPageWindow(currentPage, totalPages);

  // Only the student caller populates `reason`; hide the Type column entirely
  // for the teacher table rather than showing it blank.
  const hasReasonColumn = page?.content.some((r) => r.reason != null) ?? false;
  const columnCount = hasReasonColumn ? 5 : 4;

  // Both pickers are bounded to [registrationDate, today] in addition to
  // each other's existing cross-field constraint (from <= to). `today` is
  // always applied; the registration floor only once it's loaded.
  const today = startOfDay(new Date());
  const minSelectableDate = registrationDate ? startOfDay(registrationDate) : undefined;

  const fromDateDisabled: DateMatcher[] = [{ after: today }];
  if (minSelectableDate) fromDateDisabled.push({ before: minSelectableDate });
  if (dateTo) fromDateDisabled.push({ after: dateTo });

  const toDateDisabled: DateMatcher[] = [{ after: today }];
  if (minSelectableDate) toDateDisabled.push({ before: minSelectableDate });
  if (dateFrom) toDateDisabled.push({ before: dateFrom });

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
      {/* Filters */}
      <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <label htmlFor="coin-spend-search" className="sr-only">
            Search by {counterpartLabel.toLowerCase()}
          </label>
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <Input
            id="coin-spend-search"
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            placeholder={`Search by ${counterpartLabel.toLowerCase()}`}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <DatePickerField
            label="From date"
            placeholder="From date"
            date={dateFrom}
            onSelect={onDateFromChange}
            disabledMatcher={fromDateDisabled}
          />
          <span className="text-sm text-muted-foreground shrink-0" aria-hidden="true">
            to
          </span>
          <DatePickerField
            label="To date"
            placeholder="To date"
            date={dateTo}
            onSelect={onDateToChange}
            disabledMatcher={toDateDisabled}
          />
        </div>
      </div>

      {/* Mobile skeleton (loading only, hidden on sm+) */}
      {isLoading && (
        <div className="sm:hidden divide-y divide-slate-100">
          {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
            <div key={`m-skel-${i}`} className="px-4 py-3 motion-safe:animate-pulse space-y-1.5">
              <div className="flex justify-between">
                <div className="h-3.5 bg-slate-100 rounded w-28" />
                <div className="h-3.5 bg-slate-100 rounded w-14" />
              </div>
              <div className="h-3 bg-slate-100 rounded w-36" />
            </div>
          ))}
        </div>
      )}

      {/* State messages — rendered once (shared by mobile and desktop) */}
      {!isLoading && (isError || showTrueEmpty || showNoResults) && (
        <div className={`px-4 py-10 text-center text-sm ${isError ? "text-destructive" : "text-muted-foreground"}`}>
          {isError
            ? "Couldn't load coin spend history. Please try again later."
            : showTrueEmpty
            ? "No coin transactions yet."
            : noResultsMessage}
        </div>
      )}

      {/* Mobile card list — rows only (hidden on sm+) */}
      {!isLoading && !isError && !showTrueEmpty && !showNoResults && (
        <div className="sm:hidden divide-y divide-slate-100">
          {rows.map((row) => {
            const isClickable = row.threadId != null;
            const amountEl =
              row.amount < 0 ? (
                <span className="inline-flex items-center gap-0.5 font-bold text-rose-600 text-sm">
                  <ArrowDownRight className="h-3.5 w-3.5" aria-hidden="true" />
                  {Math.abs(row.amount)}
                </span>
              ) : row.amount > 0 ? (
                <span className="inline-flex items-center gap-0.5 font-bold text-emerald-600 text-sm">
                  <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                  {row.amount}
                </span>
              ) : (
                <span className="font-bold text-muted-foreground text-sm">—</span>
              );
            return (
              <div
                key={row.id ?? row.threadId}
                {...(isClickable
                  ? {
                      tabIndex: 0,
                      role: "button" as const,
                      onClick: () => handleRowActivate(row),
                      onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleRowActivate(row);
                        }
                      },
                    }
                  : {})}
                className={cn(
                  "px-4 py-3 flex items-center justify-between gap-3 motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                  isClickable && "cursor-pointer active:bg-slate-50",
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {row.counterpartName ?? "—"}
                    {row.reason && (
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        · {formatReasonLabel(row.reason, row.amount)}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    {formatSpendDate(row.createdAt)}
                    <span aria-hidden="true">·</span>
                    <span className="inline-flex items-center gap-0.5">
                      <Coins className="h-3 w-3 text-indigo-500" aria-hidden="true" />
                      {row.balanceAfter}
                    </span>
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-1.5">
                  {amountEl}
                  {isClickable && <ArrowRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop table — loading skeletons (hidden on mobile) */}
      {isLoading && (
        <div className="hidden sm:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">{counterpartLabel}</TableHead>
                <TableHead scope="col">Date</TableHead>
                <TableHead scope="col">Amount</TableHead>
                <TableHead scope="col">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <TableRow key={`skeleton-${i}`} className="motion-safe:animate-pulse hover:bg-transparent">
                  <TableCell><div className="h-3.5 bg-slate-100 rounded w-28" /></TableCell>
                  <TableCell><div className="h-3.5 bg-slate-100 rounded w-24" /></TableCell>
                  <TableCell><div className="h-3.5 bg-slate-100 rounded w-14" /></TableCell>
                  <TableCell><div className="h-3.5 bg-slate-100 rounded w-14" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Desktop table — row data (hidden on mobile) */}
      {!isLoading && !isError && !showTrueEmpty && !showNoResults && (
        <div className="hidden sm:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">{counterpartLabel}</TableHead>
                <TableHead scope="col">Date</TableHead>
                {hasReasonColumn && <TableHead scope="col">Type</TableHead>}
                <TableHead scope="col">Amount</TableHead>
                <TableHead scope="col">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const isClickable = row.threadId != null;
                return (
                  <TableRow
                    key={row.id ?? row.threadId}
                    {...(isClickable
                      ? {
                          tabIndex: 0,
                          role: "button" as const,
                          onClick: () => handleRowActivate(row),
                          onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleRowActivate(row);
                            }
                          },
                        }
                      : {})}
                    aria-label={
                      isClickable
                        ? `View inquiry with ${row.counterpartName ?? "unknown " + counterpartLabel.toLowerCase()}`
                        : `Support adjustment of ${Math.abs(row.amount)} coins on ${formatSpendDate(row.createdAt)}`
                    }
                    className={cn(
                      "motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                      isClickable && "cursor-pointer",
                    )}
                  >
                    <TableCell className="font-medium text-foreground">
                      {row.counterpartName ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatSpendDate(row.createdAt)}</TableCell>
                    {hasReasonColumn && (
                      <TableCell className="text-muted-foreground">
                        {row.reason ? formatReasonLabel(row.reason, row.amount) : "—"}
                      </TableCell>
                    )}
                    <TableCell>
                      {row.amount < 0 ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-rose-600">
                          <ArrowDownRight className="h-3.5 w-3.5" aria-hidden="true" />
                          {Math.abs(row.amount)}
                        </span>
                      ) : row.amount > 0 ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-emerald-600">
                          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                          {row.amount}
                        </span>
                      ) : (
                        <span className="font-semibold text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                        <Coins className="h-3.5 w-3.5 text-indigo-500" aria-hidden="true" />
                        {row.balanceAfter}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !isError && totalPages > 1 && (
        <div className="p-4 border-t border-slate-100">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("cursor-pointer", currentPage <= 0 && "pointer-events-none opacity-50")}
                  aria-disabled={currentPage <= 0}
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 0) onPageChange(currentPage - 1);
                  }}
                />
              </PaginationItem>
              {pageTokens.map((token, idx) =>
                token === ELLIPSIS ? (
                  <PaginationItem key={`ellipsis-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={token}>
                    <PaginationLink
                      href="#"
                      isActive={token === currentPage}
                      className="cursor-pointer"
                      aria-current={token === currentPage ? "page" : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(token);
                      }}
                    >
                      {token + 1}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn(
                    "cursor-pointer",
                    currentPage >= totalPages - 1 && "pointer-events-none opacity-50",
                  )}
                  aria-disabled={currentPage >= totalPages - 1}
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage < totalPages - 1) onPageChange(currentPage + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}

export default CoinSpendTable;
